// src/oura/components/aeo/QuestionAnswers.tsx
//
// The evidence under one engine's answer (#3555): why a mention was or was
// not counted, the sources it cited, and a placeholder the size of the answer
// while it loads. The question drawer on Visibility composes these around the
// answer text itself.

import { ExternalLinkIcon, InfoIcon } from "lucide-react";
import { cn } from "../../lib/cn";

/** Why a mention was or was not counted. Shown so the number reads as checked. */
export function JudgementNote({ dropped, recovered }: { dropped?: string | null; recovered?: string | null }) {
    const note = dropped || recovered;
    if (!note) return null;
    const isDropped = Boolean(dropped);
    return (
        <div
            className={cn(
                "mt-3 flex items-start gap-2 rounded-xl px-3 py-2 text-xs",
                isDropped ? "bg-amber-50 text-amber-900" : "bg-emerald-50 text-emerald-900",
            )}
        >
            <InfoIcon aria-hidden size={14} className="mt-px shrink-0" />
            <span>
                <span className="font-medium">{isDropped ? "Not counted: " : "Counted: "}</span>
                {note}
            </span>
        </div>
    );
}

/** A cited page's site, as a reader names it: no scheme, no "www.". */
function hostOf(url: string): string {
    try {
        return new URL(url).hostname.replace(/^www\./, "");
    } catch {
        return url;
    }
}

/**
 * The sources an answer cited, one chip per site, each opening the page it
 * cited off-site. A full URL with its tracking tail was one long blue word
 * per line; the site is the part a reader recognises, and the full address
 * stays on hover.
 */
export function SourceList({ sources }: { sources: string[] }) {
    if (!sources.length) return null;
    const bySite = new Map<string, string>();
    sources.forEach((url) => {
        const host = hostOf(url);
        if (!bySite.has(host)) bySite.set(host, url);
    });
    return (
        <div>
            <p className="m-0 mb-2 text-[13px] font-medium leading-[18px] text-gray-500">
                Sources <span className="font-normal text-gray-400">{bySite.size}</span>
            </p>
            {/* `list-plain`: index.css forces list markers back on every list. */}
            <ul className="list-plain m-0 flex flex-wrap gap-1.5 p-0">
                {Array.from(bySite.entries()).map(([host, url]) => (
                    <li key={host} className="min-w-0 max-w-full">
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={url}
                            className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200 hover:text-gray-900"
                        >
                            <span className="truncate">{host}</span>
                            <ExternalLinkIcon aria-hidden size={11} className="shrink-0 text-gray-400" />
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Lines sized from the answer's stored length, so the card does not jump when it lands. */
export function AnswerSkeleton({ chars }: { chars?: number | null }) {
    const lines = Math.max(2, Math.min(8, Math.round((chars || 800) / 220)));
    return (
        <div aria-hidden className="mt-2 flex flex-col gap-2">
            {Array.from({ length: lines }).map((_, i) => (
                <div
                    key={i}
                    className="h-3 animate-pulse rounded bg-gray-100"
                    style={{ width: i === lines - 1 ? "60%" : "100%" }}
                />
            ))}
        </div>
    );
}
